import React, {cloneElement, isValidElement, useEffect} from "react";
import PropTypes from "prop-types";
import {useLocation} from "react-router-dom";
import {ListItemButton, ListItemText} from "@mui/material";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import {useSidebarLayout} from "./Context/SidebarProvider";
import UpgradeAdornment from "../UpgradeAdornment";
import SidebarIcon from "./SidebarIcon";
import StyledTooltip from "../tooltips/StyledTooltip";
import {SidebarOverlay} from "./SidebarOverlay";
import {useSidebarItemStyles} from "./sidebarStyles";
import useSidebarOverlay from "./useSidebarOverlay";
import {filter} from "underscore";

const CHEV_RIGHT_WITH_TEXT = 4;
const CHEV_RIGHT_WITHOUT_TEXT = 20;

const SidebarMenu = ({children, icon, needUpgrade, text}) => {
  const {currentPage, isSidebarExpanded, isSidebarTextVisible} = useSidebarLayout();
  const {ref, isOpen: isOverlayShown, toggle, close} = useSidebarOverlay();
  const location = useLocation();

  useEffect(() => {
    close();
  }, [location.pathname, close]);

  const isCurrentParent = React.Children.toArray(children).some((child) => {
    if (isValidElement(child) && child.props.to) {
      return child.props.to.includes(currentPage);
    }
    return false;
  });

  const {classes} = useSidebarItemStyles({
    isTextHidden: !isSidebarTextVisible,
    isCurrentParent,
    inOverlay: false,
  });

  const childrenWithProps = React.Children.map(children, (child) => {
    if (isValidElement(child)) {
      return cloneElement(child, {inOverlay: true});
    }
    return child;
  });

  const visibleChildCount = filter(React.Children.toArray(children), (child) =>
    isValidElement(child),
  ).length;

  const menuButton = (
    <ListItemButton
      ref={ref}
      onClick={toggle}
      className={classes.item}
      id={`sidebar-menu-${text}`}
      tabIndex={0}
      aria-haspopup="true"
      aria-expanded={isOverlayShown}
    >
      <SidebarIcon icon={icon} />
      <ListItemText
        primary={text}
        className={isSidebarTextVisible ? classes.overlayItemText : classes.itemText}
        primaryTypographyProps={{variant: "body2"}}
      />
      {needUpgrade && <UpgradeAdornment fontSize="small" item={text} />}
      {!needUpgrade && (
        <ChevronRightIcon
          sx={{
            position: "absolute",
            right: isSidebarTextVisible ? CHEV_RIGHT_WITH_TEXT : CHEV_RIGHT_WITHOUT_TEXT,
            fontSize: isSidebarTextVisible ? "1.25rem" : "0.65rem",
          }}
        />
      )}
    </ListItemButton>
  );

  return (
    <div>
      {isSidebarExpanded ?
        (
          menuButton
        ) :
        (
          <StyledTooltip
            title={`${text} (${visibleChildCount} items)`}
            placement="right"
            opacity={isOverlayShown ? 0 : 1}
          >
            {menuButton}
          </StyledTooltip>
        )}
      {!needUpgrade && (
        <SidebarOverlay
          open={isOverlayShown}
          anchorEl={ref.current}
          onClose={close}
          parentText={!isSidebarExpanded ? text : null}
        >
          {childrenWithProps}
        </SidebarOverlay>
      )}
    </div>
  );
};

SidebarMenu.propTypes = {
  children: PropTypes.any.isRequired,
  icon: PropTypes.element.isRequired,
  needUpgrade: PropTypes.bool,
  text: PropTypes.string.isRequired,
};

export default SidebarMenu;
